/**
 * Builds the schema.org @graph for the Robux purchase page (`/{locale}/roblox`).
 *
 * Kept separate from `articleJsonLd.js` because that one is Article-centric,
 * while this page is a commercial offer: Product + AggregateOffer, plus the
 * breadcrumb and the FAQ that is already rendered on the page.
 *
 * Deliberately emits **no** AggregateRating: the page shows a Trustpilot score
 * but no review count we can verify, and marking up an unverifiable rating for
 * our own product is exactly what earns a structured-data manual action.
 *
 * SSR-safe: pure string/number work, no DOM access.
 *
 * @param {Object}  cfg
 * @param {string}  cfg.url              Canonical URL of the page.
 * @param {string}  cfg.homeUrl          Site root, used for the seller and the first crumb.
 * @param {string}  cfg.productName      e.g. "Робуксы для Roblox".
 * @param {string} [cfg.description]     Meta description / short pitch.
 * @param {string}  cfg.currency         ISO currency of the displayed rate ("RUB" / "USD").
 * @param {number} [cfg.rate]            Robux received per 1 unit of `currency`.
 * @param {number} [cfg.minRobux]        Minimum order size, in Robux.
 * @param {string}  cfg.buyLabel         Localised label for the current-page crumb.
 * @param {Array}  [cfg.faq]             [{ q, a }] pairs mirrored from the visible accordion.
 * @returns {Object} JSON-LD object with @context and @graph.
 */
export function buildRobuxPageGraph(cfg = {}) {
    const {
        url,
        homeUrl,
        productName,
        description,
        currency,
        rate,
        minRobux,
        buyLabel,
        faq,
    } = cfg;

    const product = {
        '@type': 'Product',
        '@id': `${url}#product`,
        name: productName,
        brand: { '@type': 'Brand', name: 'Roblox' },
    };
    if (description) product.description = description;

    // Cheapest thing actually purchasable here: the minimum order.
    const lowPrice = lowestPrice(rate, minRobux);
    if (lowPrice !== null) {
        product.offers = {
            '@type': 'AggregateOffer',
            url,
            priceCurrency: currency,
            lowPrice,
            availability: 'https://schema.org/InStock',
            seller: { '@type': 'Organization', name: 'AnyKod', url: homeUrl },
        };
    }

    const graph = [
        product,
        {
            '@type': 'BreadcrumbList',
            itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'AnyKod', item: homeUrl },
                { '@type': 'ListItem', position: 2, name: buyLabel, item: url },
            ],
        },
    ];

    const faqItems = normaliseFaq(faq);
    if (faqItems.length > 0) {
        graph.push({
            '@type': 'FAQPage',
            mainEntity: faqItems.map((item) => ({
                '@type': 'Question',
                name: item.q,
                acceptedAnswer: { '@type': 'Answer', text: item.a },
            })),
        });
    }

    return { '@context': 'https://schema.org', '@graph': graph };
}

/**
 * Price of the smallest possible order, rounded to cents.
 *
 * `rate` is "Robux per 1 currency unit" and, for RUB, arrives from the backend
 * formatted with a decimal comma (`number_format(..., ',')` in RobuxCalc), so
 * it is normalised before any arithmetic. Returns null when the rate is
 * missing or nonsensical, so the caller can omit the offer entirely rather
 * than publish an Infinity/NaN price.
 *
 * @param {number|string|null|undefined} rate
 * @param {number|null|undefined} minRobux
 * @returns {number|null}
 */
export function lowestPrice(rate, minRobux) {
    const parsed =
        typeof rate === 'number' ? rate : parseFloat(String(rate ?? '').replace(',', '.'));
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    if (!Number.isFinite(minRobux) || minRobux <= 0) return null;
    return Math.round((minRobux / parsed) * 100) / 100;
}

/**
 * Filters out incomplete FAQ entries and trims whitespace that some
 * translation strings carry — harmless in the accordion, ugly in the schema.
 *
 * @param {Array|null|undefined} faq
 * @returns {Array<{q: string, a: string}>}
 */
export function normaliseFaq(faq) {
    if (!Array.isArray(faq)) return [];
    return faq
        .map((item) => ({
            q: (item?.q ?? '').trim(),
            a: (item?.a ?? '').trim(),
        }))
        .filter((item) => item.q && item.a);
}
